// Sculpted bodies, the way a modeler blocks out a car: the side silhouette,
// the plan (top) outline and the front cross-section are each extruded with
// rounded edges and intersected (CSG). Wheel arches are cut from the outer
// skin only, and the glass is the window outline intersected with a slightly
// larger copy of the body, so it sits flush on every surface it crosses.
//
// UVs are re-projected for a planar livery (see livery.ts, paintPlanar):
//   side faces  → the side view (u = length, v = height), mirrored on +x so
//                 lettering reads correctly from both sides
//   top faces   → the plan view (lower half of the canvas)
import * as THREE from 'three'
import { Brush, Evaluator, INTERSECTION, SUBTRACTION, ADDITION } from 'three-bvh-csg'

export type P2 = [number, number]

export type BodySpec = {
  side: P2[] // (z, y) silhouette, starting at the front bottom, going up and over to the rear, ending at the rear bottom
  plan: P2[] // (x, z) right half outline from front to rear (mirrored for the left)
  front: P2[] // (x, y) right half cross-section from bottom to roof centre (mirrored)
  windows: P2[][] // (z, y) window outlines
  arches: { z: number; y: number; r: number }[]
  bevel: number
}

export type Sculpted = { body: THREE.BufferGeometry; glass: THREE.BufferGeometry; bounds: THREE.Box3 }

function smoothShape(points: P2[], closedStraight = true): THREE.Shape {
  const shape = new THREE.Shape()
  shape.moveTo(points[0][0], points[0][1])
  shape.splineThru(points.slice(1).map(([a, b]) => new THREE.Vector2(a, b)))
  if (closedStraight) shape.closePath()
  return shape
}

function extrude(shape: THREE.Shape, depth: number, bevel: number): THREE.BufferGeometry {
  // bevelOffset keeps the outline where it was drawn (the bevel rounds inward).
  const g = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: bevel > 0, bevelThickness: bevel, bevelSize: bevel, bevelOffset: -bevel, bevelSegments: 5, curveSegments: 48, steps: 1 })
  g.translate(0, 0, -depth / 2)
  return g
}

const mirrorHalf = (half: P2[]): P2[] => [...half, ...half.slice().reverse().map(([a, b]) => [-a, b] as P2)]

function prepare(g: THREE.BufferGeometry): THREE.BufferGeometry {
  const out = g.index ? g.toNonIndexed() : g
  out.deleteAttribute('uv')
  out.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array((out.attributes.position.count) * 2), 2))
  out.computeVertexNormals()
  return out
}

export function sculpt(spec: BodySpec): Sculpted {
  const evaluator = new Evaluator()
  evaluator.attributes = ['position', 'normal', 'uv']
  const big = 10

  // Side silhouette: shape in (z, y), extruded across x.
  const sideGeo = extrude(smoothShape(spec.side), big, spec.bevel)
  sideGeo.rotateY(-Math.PI / 2) // shape x → world z, extrusion → world x
  // Plan: shape in (x, -z) extruded up y (rotate X so extrusion goes +y).
  const planPts = mirrorHalf(spec.plan).map(([x, z]) => [x, -z] as P2)
  const planGeo = extrude(smoothShape(planPts), big, spec.bevel)
  planGeo.rotateX(-Math.PI / 2)
  // Front section: shape in (x, y) extruded along z.
  const frontPts = mirrorHalf(spec.front)
  const frontGeo = extrude(smoothShape(frontPts), big, spec.bevel * 0.8)

  const brush = (g: THREE.BufferGeometry) => {
    const b = new Brush(prepare(g))
    b.updateMatrixWorld()
    return b
  }
  let body = evaluator.evaluate(brush(sideGeo), brush(planGeo), INTERSECTION)
  body = evaluator.evaluate(body, brush(frontGeo), INTERSECTION)

  // Wheel arches: cut the outer 0.4 m of each side only.
  const bounds = new THREE.Box3().setFromBufferAttribute(body.geometry.attributes.position as THREE.BufferAttribute)
  const halfW = bounds.max.x
  for (const arch of spec.arches) {
    for (const s of [1, -1]) {
      const cyl = new THREE.CylinderGeometry(arch.r, arch.r, 0.9, 48)
      cyl.rotateZ(Math.PI / 2)
      cyl.translate(s * (halfW - 0.4 + 0.45), arch.y, arch.z)
      body = evaluator.evaluate(body, brush(cyl), SUBTRACTION)
    }
  }

  // Glass: windows × a slightly larger body.
  const grown = body.geometry.clone()
  grown.scale(1.006, 1.004, 1.002)
  const grownBrush = brush(grown)
  let glass: Brush | null = null
  for (const outline of spec.windows) {
    const prism = extrude(new THREE.Shape(outline.map(([z, y]) => new THREE.Vector2(z, y))), big, 0)
    prism.rotateY(-Math.PI / 2)
    const piece = evaluator.evaluate(brush(prism), grownBrush, INTERSECTION)
    glass = glass ? evaluator.evaluate(glass, piece, ADDITION) : piece
  }

  const bodyGeo = body.geometry
  projectUVs(bodyGeo, bounds)
  return { body: bodyGeo, glass: glass ? glass.geometry : new THREE.BufferGeometry(), bounds }
}

// Planar livery UVs (see the header). Side faces map to v ∈ [0.5, 1], plan
// faces to v ∈ [0, 0.5].
export function projectUVs(geometry: THREE.BufferGeometry, bounds: THREE.Box3) {
  const g = geometry
  const pos = g.attributes.position as THREE.BufferAttribute
  g.computeVertexNormals()
  const nor = g.attributes.normal as THREE.BufferAttribute
  const uv = new Float32Array(pos.count * 2)
  const L = bounds.max.z - bounds.min.z
  const Hh = bounds.max.y - bounds.min.y
  const Wd = bounds.max.x - bounds.min.x
  const a = new THREE.Vector3()
  const b = new THREE.Vector3()
  const c = new THREE.Vector3()
  const n = new THREE.Vector3()
  for (let i = 0; i < pos.count; i += 3) {
    a.fromBufferAttribute(pos, i)
    b.fromBufferAttribute(pos, i + 1)
    c.fromBufferAttribute(pos, i + 2)
    n.subVectors(c, b).cross(new THREE.Vector3().subVectors(a, b)).normalize()
    if (!Number.isFinite(n.x)) n.fromBufferAttribute(nor, i)
    const side = Math.abs(n.x) > Math.abs(n.y) * 0.9
    const right = (a.x + b.x + c.x) / 3 > 0
    for (let k = 0; k < 3; k++) {
      const p = k === 0 ? a : k === 1 ? b : c
      let u = (p.z - bounds.min.z) / L
      let v: number
      if (side) {
        if (right) u = 1 - u
        v = 0.5 + 0.5 * ((p.y - bounds.min.y) / Hh)
      } else {
        v = 0.5 * ((p.x - bounds.min.x) / Wd)
      }
      uv[(i + k) * 2] = u
      uv[(i + k) * 2 + 1] = v
    }
  }
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2))
}
